import * as React from "react"
import { CallParticipant } from "--client/../static_types/call.types";
import { useLoggedInUser } from "--client/common/hooks/useLoggedInUser";
import styled from "styled-components";
import { CallParticipantParagraphNode, CallParticipantSlateDocument } from "--client/../static_types/slate/CallParticipant";
import { createBlockRenderer } from "--root/slate/NodeRenderers/createBlockRenderer";
import { useAutoCompleteEditor } from "../../slate/editors/useAutoCompleteEditor";
import { SlateReactEditor, StyledStandaloneSlateInput } from "../../slate/components/SlateReactEditor";
import IcoPhone from "@floji-internal/mui-icons/Phone"
import { Strings } from "--client/../strings/en";
import { useLeafRenderer } from '--client/../slate/NodeRenderers/useLeafRenderer';
import { useDecorators } from "--root/slate/decorators/useDecorators";
import { useTelOrEmailDecorator } from "--root/slate/decorators/telOrEmail";
import { TypedSlateEditor } from "--root/slate/editors/useTypedEditor";
import { CloudIdentityBlock, CloudPersonBlock, UserBlock } from "--client/../static_types/slate/Shared";
import { AutoCompleteSearchResultsPopper } from "../../slate/autoComplete/AutoCompleteResultsRender";
import { useAutoCompleteResults } from "../../slate/autoComplete/useAutoCompleteResults";
import { SlateReactEditable } from "../../slate/components/SlateReactEditable";
import { useAutoCompletePeople } from "../../slate/autoComplete/useAutoCompletePerson";
import { POI } from "--client/../static_types/messaging.types";
import { useMessageToMessageMutation } from "../../producer/components/TopicMessageView/MessageMutationWarning";
import { ReturnGetMyAvailableCallingIntegrations, ReturnGetTopic } from "--client/../static_types/api_methods/methods.query";
import { Errors } from "--client/../Errors";
import { useAutoCompleteCloudIdentities } from "../../slate/autoComplete/useAutoCompleteCloudIdentities";
import { AutoCompleteIconSize } from "../../slate/autoComplete/useAutoCompleteResultsCache";
import { SystemDialogType } from "../../producer/components/SystemDialog/SystemDialog";
import { TopicValue } from "--client/../static_types/topic.types";

const StyledSlateInput = styled(SlateReactEditor)`
        flex: 1 0 300px;
        margin: ${props => props.theme.spacing(1)} 0 0 0;
`;

export interface CallAddressInputProps {
    setValue: (val: CallAddressInputNode[]) => void,
    onEnterPressed(): void,
    topic: ReturnGetTopic,
    supressMutationWarnings?: boolean,
    existing?: CallParticipant[],
    className?: string
}

export type CallAddressInputNode = UserBlock | CloudIdentityBlock | CloudPersonBlock | {
    type: "phone",
    phone_number: string
}

const AddressNodesFromDoc = (
    editor: TypedSlateEditor<UserBlock | CloudIdentityBlock | CloudPersonBlock | CallParticipantParagraphNode>,
    poi: POI[],
): Array<CallAddressInputNode> => {
    const internalNodes = editor.getNodes({ map: v => v.type === "user" && v || undefined })
    const cloudNodes = editor.getNodes({ map: v => (v.type === "cloudIdentity" || v.type === "cloudPerson") && v || undefined })
    const phone_numbers = poi.map((poi) => ({ type: "phone" as const, phone_number: poi.text }));
    return [...internalNodes, ...cloudNodes, ...phone_numbers]
}

const getBestIntegration = (availableIntegrations: ReturnGetMyAvailableCallingIntegrations, preffered_id?: string) => {
    if (preffered_id) {
        const pref = availableIntegrations.find(i => i.id === preffered_id);
        if (pref) return pref
    };
    const nonPersonalIntegrations = availableIntegrations.filter(v => v.owner.type !== "user");
    const [firstNonPersonalIntegration, secondNonPersonalIntegration] = nonPersonalIntegrations;
    if (firstNonPersonalIntegration && !secondNonPersonalIntegration) return firstNonPersonalIntegration;
    if (!firstNonPersonalIntegration) {
        const personalIntegrations = availableIntegrations.filter(v => v.owner.type === "user");
        const [firstPersonalIntegration, secondPersonalIntegration] = personalIntegrations;
        if (firstPersonalIntegration && !secondPersonalIntegration) return firstPersonalIntegration;
    }
    return null;
}


export const IntegrationParticipantFromNode = (
    node: Exclude<CallAddressInputNode, UserBlock | CloudPersonBlock>,
    integration: ReturnGetMyAvailableCallingIntegrations[number],
    topic: Pick<TopicValue, "type">
): CallParticipant => {
    switch (node.type) {
        case "phone":
            {
                return {
                    status: "invited",
                    user: {
                        type: "sip",
                        phone_number: node.phone_number,
                        integration_id: integration.id
                    }
                };
            }
        case "cloudIdentity":
            {
                if (topic.type.name !== "stream")
                    throw new Error("Cannot invite cloud member here");
                const matchedUser = topic.type.offnetPeople?.find(v => v.offnet_person_id === node.person_id);
                const matchedID = matchedUser?.identities.find(v => v.identity_id === node.identity_id);
                if (!matchedID)
                    throw new Errors.NotFound("Identity doesn't exist");
                return {
                    status: "invited",
                    user: {
                        type: "cloud",
                        display_name: node.children[0].text,
                        person_id: node.person_id,
                        identity_id: node.identity_id,
                        sip: {
                            integration_id: integration.id,
                            phone_number: matchedID.address
                        }
                    }
                }
            }
    }
}
export const participantsFromNodes = async (
    nodes: CallAddressInputNode[],
    topic: ReturnGetTopic,
    dialogHandler: React.Dispatch<React.SetStateAction<SystemDialogType>>,
    availableIntegrations: ReturnGetMyAvailableCallingIntegrations
): Promise<CallParticipant[] | null> => {
    const mu = nodes.map((node): { matched: CallParticipant, unmatched?: null } | { matched?: null, unmatched: Exclude<CallAddressInputNode, UserBlock | CloudPersonBlock> } => {
        switch (node.type) {
            case "user":
                return {
                    matched: {
                        user: {
                            type: "internal",
                            user_id: node.user_id,
                        },
                        status: "invited",
                    }
                }
            case "phone":
                {
                    //get the matching integration.
                    const bestIntegration = getBestIntegration(availableIntegrations);
                    if (bestIntegration)
                        return {
                            matched: IntegrationParticipantFromNode(node, bestIntegration, topic)
                        };
                    else return {
                        unmatched: node
                    }
                }
            case "cloudPerson":
                return {
                    matched: {
                        user: {
                            type: "cloud",
                            display_name: node.children[0].text,
                            person_id: node.person_id,
                        },
                        status: "invited",
                    }
                }
            case "cloudIdentity":
                {
                    if (topic.type.name !== "stream")
                        throw new Error("Cannot invite cloud member here");
                    const matchedUser = topic.type.offnetPeople?.find(v => v.offnet_person_id === node.person_id);
                    const matchedID = matchedUser?.identities.find(v => v.identity_id === node.identity_id);
                    if (!matchedID)
                        throw new Errors.NotFound("Identity doesn't exist");
                    const bestIntegration = getBestIntegration(availableIntegrations, matchedID.preferred_integration_id)
                    if (bestIntegration)
                        return {
                            matched: {
                                status: "invited",
                                user: {
                                    type: "cloud",
                                    display_name: node.children[0].text,
                                    person_id: node.person_id,
                                    identity_id: node.identity_id,
                                    sip: {
                                        integration_id: bestIntegration.id,
                                        phone_number: matchedID.address
                                    }
                                }
                            }
                        }
                    else return {
                        unmatched: node
                    }
                }
        }
    });
    const unMatched = mu.reduce((acc, curr) => curr.unmatched ? [...acc, curr.unmatched] : acc, []);
    const matched = mu.reduce((acc, curr) => curr.matched ? [...acc, curr.matched] : acc, []);
    if (unMatched.length) {
        const rematched = await new Promise<CallParticipant[] | null>((R, E) => {
            dialogHandler({
                type: "AmbiguousIntegrationCalling",
                intergrations: availableIntegrations,
                topic,
                onResolve: R,
                toBeResolved: unMatched,
                onClose() {
                    R(null);
                },
            })
        });
        if (!rematched) return null; //cancelled out
        return [...matched, ...rematched];
    } else {
        return matched
    }
}

const StyledStandaloneSlateCallAddressInput = styled(StyledStandaloneSlateInput)`
    background-color: ${p => p.theme.palette.background};
`

export const CallAddressInput: React.FC<CallAddressInputProps> = props => {
    const userId = useLoggedInUser();
    const [value, setValue] = React.useState<CallParticipantSlateDocument>([{ type: "paragraph", children: [{ text: "" }] }]);
    const mutations = useMessageToMessageMutation({
        currentMessage: value,
        topic: props.topic
    })
    const editor = useAutoCompleteEditor({
        setValue,
        value
    });
    const autoCompleteResults = useAutoCompleteResults({
        editor,
        filter: v => {
            if (v.node.type === "user" && v.node.user_id === userId.user_or_identity_id) return false
            const ret = !editor.hasNode(n => {
                if (n.type === "user")
                    return (v.node.type === "user" && n.user_id === v.node.user_id)
                if (n.type === "cloudPerson" || n.type === "cloudIdentity")
                    //you can only include a cloud person once, regardless of the target
                    return (v.node.type === "cloudPerson" || v.node.type === "cloudIdentity") && v.node.person_id === n.person_id
                return false
            })
            return ret
        },
        hooks:
            [
                useAutoCompletePeople({ allowSelf: false, matchDialPadInput: true, matchOnEmpty: false }),
                useAutoCompleteCloudIdentities({
                    secondaryPerson: <Strings.Inline>Floji call</Strings.Inline>,
                    secondaryIdentity: id => <Strings.Inline>Call {id.address}</Strings.Inline>,
                    streamId: props.topic.id, identityTypes: {
                        email: false,
                        sip: { icon: <IcoPhone size={AutoCompleteIconSize} /> },
                        webchat: false,
                        whatsapp: { icon: <IcoPhone size={AutoCompleteIconSize} /> },
                    },
                    dedupe: true
                })
            ]
    })
    const decorate = useDecorators(editor,
        [
            useTelOrEmailDecorator({ types: "tel" })
        ]);

    const renderer = React.useMemo(() => createBlockRenderer({ editor, mentionsContext: { closeIcon: true, linking: false, topic: props.topic } }), []);
    const leafRenderer = useLeafRenderer();
    const current = React.useRef<CallAddressInputNode[]>([]);
    React.useEffect(() => {
        const newParts = AddressNodesFromDoc(editor, decorate.results)
        if (JSON.stringify(current.current) !== JSON.stringify(newParts)) {
            current.current = newParts
            props.setValue(newParts)
        }
    }, [value, decorate.results]);
    return <>
        <AutoCompleteSearchResultsPopper
            editor={editor}
            results={autoCompleteResults}
        />

        <StyledSlateInput editor={editor} className={props.className}>
            <StyledStandaloneSlateCallAddressInput>
                <SlateReactEditable
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation()
                            props.onEnterPressed();
                        }
                    }}
                    decorate={decorate}
                    placeholder={Strings.inline("Start typing names or numbers")}
                    autoFocus
                    renderElement={renderer}
                    renderLeaf={leafRenderer}
                />
                {props.supressMutationWarnings ? null : mutations}
            </StyledStandaloneSlateCallAddressInput>
        </StyledSlateInput>
    </>
}
